// 🔧 가격 데이터 디버거 - 웨어러블 데이터 복구용
// [배경] 정합성 검증에서 새 형식 priceId로 빈 문서가 생성되어 기존 가격과 불일치 발생
// [사용] 브라우저 콘솔에서 실행하여 문제 진단 및 복구

import { 
  collection, 
  getDocs, 
  deleteDoc,
  doc,
  query,
  where 
} from 'firebase/firestore';
import { db } from '../firebase';
import { generatePriceId } from '../types/deviceCatalog';

export interface PriceDocumentInfo {
  id: string;
  modelId: string;
  brand: string;
  deviceType: string;
  storage?: string;
  connectivity?: string;
  gradePrices: { A: number; B: number; C: number; D: number; E: number };
  isEmptyPrice: boolean;
}

/**
 * 🔍 unified_prices 컬렉션의 모든 문서 조회
 */
export const getAllUnifiedPrices = async (): Promise<PriceDocumentInfo[]> => {
  const snapshot = await getDocs(collection(db, 'unified_prices'));
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    const gradePrices = data.gradePrices || { A: 0, B: 0, C: 0, D: 0 };
    
    return {
      id: doc.id,
      modelId: data.modelId,
      brand: data.brand,
      deviceType: data.deviceType,
      storage: data.variant?.storage,
      connectivity: data.variant?.connectivity,
      gradePrices,
      isEmptyPrice: gradePrices.A === 0 && gradePrices.B === 0 && 
                    gradePrices.C === 0 && gradePrices.D === 0
    };
  });
};

/**
 * 🔍 웨어러블 가격 데이터 분석
 */
export const analyzeWearablePrices = async (brand: 'Apple' | 'Samsung' | 'LG' = 'Apple') => {
  console.log(`\n🔍 ${brand} 웨어러블 가격 데이터 분석 시작...\n`);
  
  const allPrices = await getAllUnifiedPrices();
  
  // 웨어러블만 필터링
  const wearablePrices = allPrices.filter(p => 
    p.brand === brand && p.deviceType === 'wearable'
  );
  
  console.log(`📊 전체 웨어러블 가격 문서: ${wearablePrices.length}개`);
  
  // 빈 가격과 실제 가격 분류
  const emptyPrices = wearablePrices.filter(p => p.isEmptyPrice);
  const filledPrices = wearablePrices.filter(p => !p.isEmptyPrice);
  
  console.log(`\n🔴 빈 가격 문서 (삭제 대상): ${emptyPrices.length}개`);
  emptyPrices.forEach(p => {
    console.log(`   - ${p.id} (modelId: ${p.modelId})`);
  });
  
  console.log(`\n🟢 실제 가격이 있는 문서: ${filledPrices.length}개`);
  filledPrices.forEach(p => {
    console.log(`   - ${p.id}`);
    console.log(`     modelId: ${p.modelId}`);
    console.log(`     가격: A=${p.gradePrices.A.toLocaleString()}, B=${p.gradePrices.B.toLocaleString()}, C=${p.gradePrices.C.toLocaleString()}, D=${p.gradePrices.D.toLocaleString()}`);
  });
  
  // priceId 형식 분석
  console.log(`\n📝 priceId 형식 분석:`);
  
  const priceIdPatterns: Record<string, number> = {};
  wearablePrices.forEach(p => {
    // priceId에서 패턴 추출 (예: apple-wearable-...-kr)
    const parts = p.id.split('-');
    const pattern = parts.length > 3 ? `${parts.length}파트 (${parts.slice(-1)[0]} 접미사)` : `${parts.length}파트`;
    priceIdPatterns[pattern] = (priceIdPatterns[pattern] || 0) + 1;
  });
  
  Object.entries(priceIdPatterns).forEach(([pattern, count]) => {
    console.log(`   - ${pattern}: ${count}개`);
  });
  
  return {
    total: wearablePrices.length,
    empty: emptyPrices,
    filled: filledPrices
  };
};

/**
 * 🗑️ 빈 가격 문서 삭제 (복구용)
 */
export const deleteEmptyPriceDocuments = async (
  brand: 'Apple' | 'Samsung' | 'LG' = 'Apple',
  deviceType: 'wearable' | 'tablet' | 'smartphone' = 'wearable',
  dryRun: boolean = true
): Promise<{ deleted: number; skipped: number }> => {
  console.log(`\n🗑️ 빈 가격 문서 삭제 ${dryRun ? '(DRY RUN - 실제 삭제 안함)' : '(실제 삭제!)'}\n`);
  
  const allPrices = await getAllUnifiedPrices();
  
  // 대상 필터링
  const targetPrices = allPrices.filter(p => 
    p.brand === brand && 
    p.deviceType === deviceType &&
    p.isEmptyPrice
  );
  
  console.log(`📊 삭제 대상: ${targetPrices.length}개`);
  
  let deleted = 0;
  let skipped = 0;
  
  for (const price of targetPrices) {
    if (dryRun) {
      console.log(`   [DRY] 삭제 예정: ${price.id}`);
      deleted++;
    } else {
      try {
        await deleteDoc(doc(db, 'unified_prices', price.id));
        console.log(`   ✅ 삭제됨: ${price.id}`);
        deleted++;
      } catch (error) {
        console.error(`   ❌ 삭제 실패: ${price.id}`, error);
        skipped++;
      }
    }
  }
  
  console.log(`\n🎉 완료: ${deleted}개 삭제${dryRun ? ' (예정)' : ''}, ${skipped}개 실패`);
  
  return { deleted, skipped };
};

/**
 * 🔄 priceId 불일치 분석 (정합성 검증 vs 실제 데이터)
 */
export const analyzePriceIdMismatch = async (
  brand: 'Apple' | 'Samsung' | 'LG' = 'Apple',
  deviceType: 'wearable' | 'tablet' | 'smartphone' = 'wearable'
) => {
  console.log(`\n🔄 priceId 불일치 분석: ${brand} ${deviceType}\n`);
  
  // 1. device_catalog에서 모델 조회
  const { DeviceCatalogService } = await import('../services/deviceCatalogService');
  const models = await DeviceCatalogService.getModelsByBrandAndCategory(brand, deviceType);
  
  console.log(`📱 모델 수: ${models.length}개`);
  
  // 2. unified_prices에서 가격 조회
  const allPrices = await getAllUnifiedPrices();
  const prices = allPrices.filter(p => p.brand === brand && p.deviceType === deviceType);
  
  console.log(`💰 가격 문서 수: ${prices.length}개`);
  
  // 3. 각 모델별로 매칭 분석
  console.log(`\n📊 모델별 매칭 분석:\n`);
  
  const mismatches: Array<{
    modelId: string;
    modelName: string;
    expectedPriceId: string;
    actualPriceIds: string[];
    hasFilledPrice: boolean;
  }> = [];
  
  models.forEach(model => {
    // 정합성 검증에서 사용하는 priceId 생성
    const expectedPriceId = generatePriceId(
      model.id,
      { 
        storage: model.storageOptions[0],
        connectivity: undefined
      },
      'KR'
    );
    
    // 실제 가격 문서에서 modelId로 찾기
    const matchingPrices = prices.filter(p => 
      p.modelId === model.id || 
      p.id.includes(model.id.toLowerCase()) ||
      model.id.toLowerCase().includes(p.modelId?.toLowerCase() || '')
    );
    
    const hasFilledPrice = matchingPrices.some(p => !p.isEmptyPrice);
    
    console.log(`\n  📱 ${model.modelKo || model.id}`);
    console.log(`     model.id: ${model.id}`);
    console.log(`     expectedPriceId: ${expectedPriceId}`);
    console.log(`     매칭된 가격 문서: ${matchingPrices.length}개`);
    
    matchingPrices.forEach(p => {
      const status = p.isEmptyPrice ? '🔴 빈 가격' : '🟢 가격 있음';
      console.log(`       - ${p.id} ${status}`);
      if (!p.isEmptyPrice) {
        console.log(`         (A=${p.gradePrices.A.toLocaleString()}, B=${p.gradePrices.B.toLocaleString()})`);
      }
    });
    
    // 불일치 기록
    if (matchingPrices.length > 0 && expectedPriceId !== matchingPrices[0]?.id) {
      mismatches.push({
        modelId: model.id,
        modelName: model.modelKo || model.id,
        expectedPriceId,
        actualPriceIds: matchingPrices.map(p => p.id),
        hasFilledPrice
      });
    }
  });
  
  // 4. 불일치 요약
  console.log(`\n\n🔍 priceId 불일치 요약:`);
  console.log(`   총 불일치: ${mismatches.length}개`);
  
  const withFilledPrice = mismatches.filter(m => m.hasFilledPrice);
  console.log(`   가격이 설정된 불일치: ${withFilledPrice.length}개`);
  
  if (withFilledPrice.length > 0) {
    console.log(`\n⚠️ 중요: 다음 모델들은 기존 가격 데이터가 있지만 priceId 형식이 다릅니다:`);
    withFilledPrice.forEach(m => {
      console.log(`   - ${m.modelName}`);
      console.log(`     예상 priceId: ${m.expectedPriceId}`);
      console.log(`     실제 priceId: ${m.actualPriceIds.join(', ')}`);
    });
  }
  
  return { models: models.length, prices: prices.length, mismatches };
};

/**
 * 🔧 웨어러블 가격 데이터 복구 - 빈 문서 삭제
 * 
 * 사용법:
 * 1. 브라우저 콘솔에서:
 *    import { recoverWearablePrices } from './utils/priceDataDebugger';
 *    await recoverWearablePrices('Apple', true);  // dry run
 *    await recoverWearablePrices('Apple', false); // 실제 삭제
 */
export const recoverWearablePrices = async (
  brand: 'Apple' | 'Samsung' | 'LG' = 'Apple',
  dryRun: boolean = true
) => {
  console.log(`\n🔧 웨어러블 가격 데이터 복구 시작...\n`);
  
  // 1. 현재 상태 분석
  const analysis = await analyzeWearablePrices(brand);
  
  if (analysis.empty.length === 0) {
    console.log(`\n✅ 빈 가격 문서가 없습니다. 복구할 필요 없음.`);
    return;
  }
  
  // 2. 빈 문서 삭제
  const result = await deleteEmptyPriceDocuments(brand, 'wearable', dryRun);
  
  // 3. 불일치 분석
  if (!dryRun) {
    console.log(`\n🔄 삭제 후 상태 재분석...`);
    await analyzePriceIdMismatch(brand, 'wearable');
  }
  
  return result;
};

/**
 * 🔍 중복 가격 문서 찾기 - 동일 모델에 여러 priceId가 있는 경우
 */
export interface DuplicatePriceInfo {
  modelId: string;
  documents: Array<{
    priceId: string;
    hasPrice: boolean;
    gradePrices: { A: number; B: number; C: number; D: number; E: number };
  }>;
  recommendation: 'keep_filled' | 'manual_review';
}

export const findDuplicatePriceDocuments = async (
  brand: 'Apple' | 'Samsung' | 'LG' = 'Apple',
  deviceType: 'wearable' | 'tablet' | 'smartphone' = 'wearable'
): Promise<DuplicatePriceInfo[]> => {
  console.log(`\n🔍 중복 가격 문서 검색: ${brand} ${deviceType}\n`);
  
  const allPrices = await getAllUnifiedPrices();
  const prices = allPrices.filter(p => p.brand === brand && p.deviceType === deviceType);
  
  // modelId별로 그룹핑
  const groupedByModelId: Record<string, PriceDocumentInfo[]> = {};
  
  prices.forEach(price => {
    const key = price.modelId || 'unknown';
    if (!groupedByModelId[key]) {
      groupedByModelId[key] = [];
    }
    groupedByModelId[key].push(price);
  });
  
  // 중복 찾기 (2개 이상의 문서가 있는 경우)
  const duplicates: DuplicatePriceInfo[] = [];
  
  Object.entries(groupedByModelId).forEach(([modelId, docs]) => {
    if (docs.length > 1) {
      const hasFilledDoc = docs.some(d => !d.isEmptyPrice);
      const hasEmptyDoc = docs.some(d => d.isEmptyPrice);
      
      duplicates.push({
        modelId,
        documents: docs.map(d => ({
          priceId: d.id,
          hasPrice: !d.isEmptyPrice,
          gradePrices: d.gradePrices
        })),
        recommendation: hasFilledDoc && hasEmptyDoc ? 'keep_filled' : 'manual_review'
      });
    }
  });
  
  // 결과 출력
  console.log(`📊 중복 발견: ${duplicates.length}개 모델\n`);
  
  duplicates.forEach((dup, idx) => {
    console.log(`${idx + 1}. 📱 modelId: ${dup.modelId}`);
    console.log(`   추천: ${dup.recommendation === 'keep_filled' ? '🟢 빈 문서 삭제 가능' : '⚠️ 수동 확인 필요'}`);
    console.log(`   문서:`);
    dup.documents.forEach(doc => {
      const status = doc.hasPrice ? '🟢 가격있음' : '🔴 빈문서';
      const priceInfo = doc.hasPrice 
        ? `(A=${doc.gradePrices.A.toLocaleString()}, B=${doc.gradePrices.B.toLocaleString()})` 
        : '';
      console.log(`     - ${doc.priceId} ${status} ${priceInfo}`);
    });
    console.log('');
  });
  
  return duplicates;
};

/**
 * 🗑️ 중복 가격 문서 정리 - 빈 문서만 삭제하고 가격 있는 문서 보존
 */
export const cleanupDuplicatePrices = async (
  brand: 'Apple' | 'Samsung' | 'LG' = 'Apple',
  deviceType: 'wearable' | 'tablet' | 'smartphone' = 'wearable',
  dryRun: boolean = true
): Promise<{ deleted: number; skipped: number; kept: number }> => {
  console.log(`\n🗑️ 중복 가격 정리 ${dryRun ? '(DRY RUN)' : '(실제 삭제!)'}\n`);
  
  const duplicates = await findDuplicatePriceDocuments(brand, deviceType);
  
  let deleted = 0;
  let skipped = 0;
  let kept = 0;
  
  for (const dup of duplicates) {
    const filledDocs = dup.documents.filter(d => d.hasPrice);
    const emptyDocs = dup.documents.filter(d => !d.hasPrice);
    
    // 가격 있는 문서가 있으면 빈 문서만 삭제
    if (filledDocs.length > 0 && emptyDocs.length > 0) {
      kept += filledDocs.length;
      
      for (const emptyDoc of emptyDocs) {
        if (dryRun) {
          console.log(`   [DRY] 삭제 예정: ${emptyDoc.priceId} (빈 문서)`);
          deleted++;
        } else {
          try {
            await deleteDoc(doc(db, 'unified_prices', emptyDoc.priceId));
            console.log(`   ✅ 삭제됨: ${emptyDoc.priceId}`);
            deleted++;
          } catch (error) {
            console.error(`   ❌ 삭제 실패: ${emptyDoc.priceId}`, error);
            skipped++;
          }
        }
      }
    } else if (filledDocs.length === 0) {
      // 모든 문서가 빈 경우 - 하나만 남기고 나머지 삭제
      console.log(`   ⚠️ ${dup.modelId}: 모든 문서가 빈 가격 - 첫 번째 문서만 유지`);
      const [keepDoc, ...removeDocks] = emptyDocs;
      kept++;
      
      for (const removeDoc of removeDocks) {
        if (dryRun) {
          console.log(`   [DRY] 삭제 예정: ${removeDoc.priceId} (중복 빈 문서)`);
          deleted++;
        } else {
          try {
            await deleteDoc(doc(db, 'unified_prices', removeDoc.priceId));
            console.log(`   ✅ 삭제됨: ${removeDoc.priceId}`);
            deleted++;
          } catch (error) {
            console.error(`   ❌ 삭제 실패: ${removeDoc.priceId}`, error);
            skipped++;
          }
        }
      }
    } else {
      // 모든 문서에 가격이 있는 경우 - 수동 확인 필요
      console.log(`   ⚠️ ${dup.modelId}: 여러 문서에 가격이 설정됨 - 수동 확인 필요`);
      skipped += dup.documents.length;
    }
  }
  
  console.log(`\n🎉 정리 완료!`);
  console.log(`   삭제: ${deleted}개${dryRun ? ' (예정)' : ''}`);
  console.log(`   유지: ${kept}개`);
  console.log(`   건너뜀: ${skipped}개`);
  
  return { deleted, skipped, kept };
};

/**
 * 📊 전체 중복 상황 요약
 */
export const summarizeDuplicates = async (
  brand: 'Apple' | 'Samsung' | 'LG' = 'Apple',
  deviceType: 'wearable' | 'tablet' | 'smartphone' = 'wearable'
) => {
  console.log(`\n📊 ${brand} ${deviceType} 중복 가격 문서 요약\n`);
  console.log('='.repeat(60) + '\n');
  
  const duplicates = await findDuplicatePriceDocuments(brand, deviceType);
  
  const safeToDelete = duplicates.filter(d => d.recommendation === 'keep_filled');
  const needsReview = duplicates.filter(d => d.recommendation === 'manual_review');
  
  console.log('\n' + '='.repeat(60));
  console.log(`\n📋 요약:`);
  console.log(`   • 총 중복 모델: ${duplicates.length}개`);
  console.log(`   • 🟢 안전하게 정리 가능: ${safeToDelete.length}개 (빈 문서 삭제)`);
  console.log(`   • ⚠️ 수동 확인 필요: ${needsReview.length}개`);
  
  const totalDocsToDelete = safeToDelete.reduce((sum, d) => 
    sum + d.documents.filter(doc => !doc.hasPrice).length, 0
  );
  
  console.log(`   • 삭제 예정 문서 수: ${totalDocsToDelete}개`);
  
  console.log(`\n💡 정리하려면 다음 명령어를 실행하세요:`);
  console.log(`   await priceDebugger.cleanupDuplicatePrices('${brand}', '${deviceType}', true);  // 미리보기`);
  console.log(`   await priceDebugger.cleanupDuplicatePrices('${brand}', '${deviceType}', false); // 실제 삭제\n`);
  
  return {
    duplicates,
    safeToDelete,
    needsReview,
    totalDocsToDelete
  };
};

// 글로벌 노출 (브라우저 콘솔에서 사용 가능)
if (typeof window !== 'undefined') {
  (window as any).priceDebugger = {
    getAllUnifiedPrices,
    analyzeWearablePrices,
    deleteEmptyPriceDocuments,
    analyzePriceIdMismatch,
    recoverWearablePrices,
    findDuplicatePriceDocuments,
    cleanupDuplicatePrices,
    summarizeDuplicates
  };
  
  console.log(`
🔧 가격 데이터 디버거 로드됨!

📊 분석 명령어:
  await priceDebugger.analyzeWearablePrices('Apple');
  await priceDebugger.analyzePriceIdMismatch('Apple', 'wearable');
  await priceDebugger.summarizeDuplicates('Apple', 'wearable');

🔍 중복 문서 확인:
  await priceDebugger.findDuplicatePriceDocuments('Apple', 'wearable');

🗑️ 정리 명령어:
  await priceDebugger.cleanupDuplicatePrices('Apple', 'wearable', true);  // 미리보기
  await priceDebugger.cleanupDuplicatePrices('Apple', 'wearable', false); // 실제 삭제
  await priceDebugger.recoverWearablePrices('Apple', false); // 빈 문서 전체 삭제
`);
}

export default {
  getAllUnifiedPrices,
  analyzeWearablePrices,
  deleteEmptyPriceDocuments,
  analyzePriceIdMismatch,
  recoverWearablePrices,
  findDuplicatePriceDocuments,
  cleanupDuplicatePrices,
  summarizeDuplicates
};

